import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileSignature, Upload, Megaphone, FileText, AlertTriangle, CalendarRange, Sparkles, Users, Search, CheckCircle2, Send, Eye, X, Copy, ShieldCheck, Archive, Radio } from "lucide-react";
import { useAuth } from "../lib/auth";

const LETTER_TYPES = [
  { id: "business_communication", label: "Comunicazione aziendale", icon: Megaphone, color: "text-blue-600 bg-blue-50" },
  { id: "formal_letter",          label: "Lettera missiva",         icon: FileText,   color: "text-purple-600 bg-purple-50" },
  { id: "service_interruption",   label: "Interruzione servizio",   icon: AlertTriangle, color: "text-amber-600 bg-amber-50" },
  { id: "holiday_plan",           label: "Piano ferie",             icon: CalendarRange, color: "text-green-600 bg-green-50" },
  { id: "union_assembly_report",  label: "Rapporto assemblea sindacale", icon: Users, color: "text-red-600 bg-red-50" },
];

export default function DocumentsPage() {
  const { user } = useAuth();
  const isSubAdmin = user?.role === "admin" || user?.role === "sub_admin" || user?.role === "superadmin";
  const [tab, setTab] = useState("letters");
  const [items, setItems] = useState([]);
  const [communications, setCommunications] = useState([]);
  // Broadcast
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastFile, setBroadcastFile] = useState(null);
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastMode, setBroadcastMode] = useState("all"); // all | single
  const [broadcastEmpId, setBroadcastEmpId] = useState("");
  const [broadcastEmpSearch, setBroadcastEmpSearch] = useState("");
  const [broadcastEmpResults, setBroadcastEmpResults] = useState([]);
  const [broadcastSending, setBroadcastSending] = useState(false);
  // Letter
  const [letterType, setLetterType] = useState("business_communication");
  const [letterForm, setLetterForm] = useState({ title: "", subject: "", body: "", sign_by: "", recipient_employee_id: "", service_name: "", interruption_from: "", interruption_to: "", holiday_from: "", holiday_to: "" });
  const [empSearch, setEmpSearch] = useState(""); const [empResults, setEmpResults] = useState([]);
  // Upload
  const [uploadFile, setUploadFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Signature requests
  const [signOpen, setSignOpen] = useState(false);
  const [signDoc, setSignDoc] = useState(null);
  const [signRecipients, setSignRecipients] = useState([]); // [{id,name,lastname,email}]
  const [signSearch, setSignSearch] = useState("");
  const [signSearchResults, setSignSearchResults] = useState([]);
  const [signNote, setSignNote] = useState("");
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [docSignatureRequests, setDocSignatureRequests] = useState({}); // doc_id -> [requests]

  const load = async () => { try { setItems((await api.get("/documents")).data); } catch (_) { /* noop */ } };
  const loadCommunications = async () => { try { setCommunications((await api.get("/documents/communications")).data); } catch (_) { /* noop */ } };
  useEffect(() => { load(); loadCommunications(); }, []);

  // Autocomplete broadcast
  useEffect(() => {
    if (!broadcastEmpSearch || broadcastEmpSearch.length < 2) { setBroadcastEmpResults([]); return; }
    const t = setTimeout(async () => {
      try { setBroadcastEmpResults((await api.get(`/employees/search?q=${encodeURIComponent(broadcastEmpSearch)}`)).data); } catch (_) { /* noop */ }
    }, 200);
    return () => clearTimeout(t);
  }, [broadcastEmpSearch]);

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const submitBroadcast = async () => {
    if (!broadcastFile) return toast.error("Seleziona un file");
    if (!broadcastTitle.trim()) return toast.error("Indica un titolo");
    if (broadcastMode === "single" && !broadcastEmpId) return toast.error("Seleziona un dipendente");
    setBroadcastSending(true);
    try {
      const b64 = await fileToBase64(broadcastFile);
      const payload = {
        title: broadcastTitle,
        content_base64: b64,
        kind: broadcastFile.type.startsWith("image/") ? "image" : "pdf",
        employee_ids: broadcastMode === "single" ? [broadcastEmpId] : null,
      };
      const r = await api.post("/documents/broadcast", payload);
      toast.success(`Inviato a ${r.data.recipients} dipendenti`);
      setBroadcastOpen(false);
      setBroadcastFile(null); setBroadcastTitle(""); setBroadcastEmpId(""); setBroadcastEmpSearch("");
      loadCommunications();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBroadcastSending(false); }
  };

  useEffect(() => {
    if (!empSearch) { setEmpResults([]); return; }
    const t = setTimeout(async () => {
      try { setEmpResults((await api.get(`/employees/search?q=${encodeURIComponent(empSearch)}`)).data); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [empSearch]);

  const createLetter = async () => {
    if (!letterForm.title) { toast.error("Titolo obbligatorio"); return; }
    try {
      await api.post("/documents/letters", { letter_type: letterType, ...letterForm });
      toast.success("Lettera generata come PDF");
      setLetterForm({ title: "", subject: "", body: "", sign_by: "", recipient_employee_id: "", service_name: "", interruption_from: "", interruption_to: "", holiday_from: "", holiday_to: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setUploadFile({ file: f, content: reader.result });
    reader.readAsDataURL(f);
  };

  const doUpload = async () => {
    if (!uploadFile) { toast.error("Seleziona un file"); return; }
    setUploading(true); setAnalysis(null);
    try {
      const r = await api.post("/documents/upload", {
        filename: uploadFile.file.name,
        content_base64: uploadFile.content,
        kind: uploadFile.file.type === "application/pdf" ? "pdf" : "image",
        auto_analyze: true,
      });
      toast.success("Documento caricato");
      setAnalysis(r.data.analysis || null);
      setUploadFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setUploading(false); }
  };

  const splitSend = async (docId) => {
    try {
      const r = await api.post(`/documents/${docId}/split-send`);
      toast.success(`${r.data.copies_created} copie create · ${r.data.emails_sent} email inviate`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // ---------- SIGNATURE REQUESTS ----------
  const openSignDialog = async (doc) => {
    setSignDoc(doc);
    setSignRecipients([]);
    setSignSearch("");
    setSignSearchResults([]);
    setSignNote("");
    setSignOpen(true);
    try {
      const r = await api.get(`/documents/${doc.id}/signature-requests`);
      setDocSignatureRequests((prev) => ({ ...prev, [doc.id]: r.data || [] }));
    } catch {}
  };

  useEffect(() => {
    if (!signSearch) { setSignSearchResults([]); return; }
    const t = setTimeout(async () => {
      try { setSignSearchResults((await api.get(`/employees/search?q=${encodeURIComponent(signSearch)}`)).data); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [signSearch]);

  const addSignRecipient = (emp) => {
    if (signRecipients.find((e) => e.id === emp.id)) return;
    setSignRecipients((prev) => [...prev, emp]);
    setSignSearch("");
    setSignSearchResults([]);
  };

  const removeSignRecipient = (id) => setSignRecipients((prev) => prev.filter((e) => e.id !== id));

  const submitSignatureRequests = async () => {
    if (!signDoc || signRecipients.length === 0) { toast.error("Aggiungi almeno un destinatario"); return; }
    setSignSubmitting(true);
    try {
      const r = await api.post(`/documents/${signDoc.id}/signature-requests`, {
        document_id: signDoc.id,
        signer_employee_ids: signRecipients.map((e) => e.id),
        signer_user_ids: [],
        note: signNote,
      });
      toast.success(`${r.data.created} richieste di firma inviate`);
      // refresh requests for this doc
      const rr = await api.get(`/documents/${signDoc.id}/signature-requests`);
      setDocSignatureRequests((prev) => ({ ...prev, [signDoc.id]: rr.data || [] }));
      setSignRecipients([]);
      setSignNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richieste");
    } finally { setSignSubmitting(false); }
  };

  const copySignLink = (req) => {
    const base = window.location.origin;
    const link = `${base}/sign/${req.id}?token=${req.token}`;
    navigator.clipboard?.writeText(link).then(
      () => toast.success("Link firma copiato"),
      () => toast.error("Copia non riuscita")
    );
  };

  const revokeSignReq = async (req) => {
    try {
      await api.post(`/signature-requests/${req.id}/revoke`);
      toast.success("Richiesta revocata");
      const rr = await api.get(`/documents/${signDoc.id}/signature-requests`);
      setDocSignatureRequests((prev) => ({ ...prev, [signDoc.id]: rr.data || [] }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`);
      const a = document.createElement("a");
      a.href = r.data.content;
      a.download = `${(r.data.title || "documento").replace(/[^\w\-]+/g, "_")}.${r.data.kind === "pdf" ? "pdf" : "png"}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Download non disponibile"); }
  };

  const needsRecipient = letterType === "formal_letter";
  const ltype = LETTER_TYPES.find((t) => t.id === letterType);

  return (
    <div className="p-4 md:p-12" data-testid="documents-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Documenti · Lettere · AI</div>
        <h1 className="font-display text-4xl">Documenti</h1>
        <p className="text-[#555] mt-1 text-sm">Genera lettere predefinite · Upload con riconoscimento Codice Fiscale · Firma digitale</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap items-center">
        {[
          { id: "letters", label: "Genera lettera", icon: FileSignature },
          { id: "upload", label: "Upload con AI", icon: Upload },
          { id: "list", label: `Archivio (${items.length})`, icon: FileText },
          { id: "communications", label: `Comunicazioni (${communications.length})`, icon: Archive },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`docs-tab-${t.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-all ${tab === t.id ? "bg-[#FF7A29] text-white shadow-lg shadow-orange-200" : "bg-white border border-[#EDE5D8] hover:border-[#FF7A29]"}`}>
              <Icon className="w-4 h-4"/> {t.label}
            </button>
          );
        })}
        {isSubAdmin && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              onClick={()=>{ setBroadcastMode("single"); setBroadcastOpen(true); }}
              data-testid="broadcast-doc-single-btn"
              variant="outline"
              className="rounded-full border-[#FF7A29] text-[#E85D00] hover:bg-[#FFF0E3] h-10"
            >
              <Search className="w-4 h-4 mr-2"/> Invia a singolo dipendente
            </Button>
            <Button
              onClick={()=>{ setBroadcastMode("all"); setBroadcastOpen(true); }}
              data-testid="broadcast-doc-btn"
              className="rounded-full bg-black text-white hover:bg-[#FF7A29] h-10"
            >
              <Radio className="w-4 h-4 mr-2"/> Invia a tutti i dipendenti
            </Button>
          </div>
        )}
      </div>

      {/* LETTERE */}
      {tab === "letters" && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="card-smart p-4 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-2">Tipo lettera</div>
            {LETTER_TYPES.map((t) => {
              const Icon = t.icon;
              const active = letterType === t.id;
              return (
                <button key={t.id} onClick={()=>setLetterType(t.id)} data-testid={`letter-type-${t.id}`}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${active ? "bg-[#FF7A29] text-white" : "hover:bg-[#FFF0E3]"}`}>
                  <Icon className="w-4 h-4"/>
                  <span className="text-sm font-semibold">{t.label}</span>
                </button>
              );
            })}
          </div>
          <div className="md:col-span-3 card-smart p-6 space-y-4">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ltype?.color}`}>
              <ltype.icon className="w-3.5 h-3.5"/>
              <span className="text-xs font-semibold uppercase tracking-widest">{ltype?.label}</span>
            </div>

            <div><Label>Titolo documento *</Label><Input data-testid="letter-title" className="rounded-xl mt-1" value={letterForm.title} onChange={(e)=>setLetterForm({...letterForm, title: e.target.value})} placeholder="Es. Orario estivo 2026"/></div>
            <div><Label>Oggetto</Label><Input className="rounded-xl mt-1" value={letterForm.subject} onChange={(e)=>setLetterForm({...letterForm, subject: e.target.value})}/></div>

            {/* Campi specifici */}
            {letterType === "service_interruption" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-3"><Label>Servizio</Label><Input className="rounded-xl mt-1" value={letterForm.service_name} onChange={(e)=>setLetterForm({...letterForm, service_name: e.target.value})} placeholder="Es. mensa aziendale"/></div>
                <div><Label>Dal</Label><Input type="date" className="rounded-xl mt-1" value={letterForm.interruption_from} onChange={(e)=>setLetterForm({...letterForm, interruption_from: e.target.value})}/></div>
                <div><Label>Al</Label><Input type="date" className="rounded-xl mt-1" value={letterForm.interruption_to} onChange={(e)=>setLetterForm({...letterForm, interruption_to: e.target.value})}/></div>
              </div>
            )}
            {letterType === "holiday_plan" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Periodo ferie · Dal</Label><Input type="date" className="rounded-xl mt-1" value={letterForm.holiday_from} onChange={(e)=>setLetterForm({...letterForm, holiday_from: e.target.value})}/></div>
                <div><Label>Periodo ferie · Al</Label><Input type="date" className="rounded-xl mt-1" value={letterForm.holiday_to} onChange={(e)=>setLetterForm({...letterForm, holiday_to: e.target.value})}/></div>
              </div>
            )}

            {/* Destinatario (obbligatorio per lettera missiva) */}
            {needsRecipient && (
              <div>
                <Label>Destinatario — cerca dipendente</Label>
                <Input data-testid="letter-recipient-search" className="rounded-xl mt-1" value={empSearch} onChange={(e)=>setEmpSearch(e.target.value)} placeholder="Nome, cognome o email…"/>
                {empResults.length > 0 && (
                  <div className="border border-[#EDE5D8] rounded-xl mt-1 max-h-48 overflow-auto divide-y divide-[#EDE5D8]">
                    {empResults.map((e) => (
                      <button key={e.id} type="button" onClick={()=>{setLetterForm({...letterForm, recipient_employee_id: e.id}); setEmpSearch(`${e.name} ${e.lastname}`); setEmpResults([]);}} className="w-full text-left px-3 py-2 hover:bg-[#FFF0E3]">
                        <div className="text-sm font-semibold">{e.name} {e.lastname}</div>
                        <div className="text-xs text-[#757575]">{e.email}{e.job && ` · ${e.job}`}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div><Label>Corpo della lettera</Label><Textarea data-testid="letter-body" className="rounded-xl mt-1 min-h-40" value={letterForm.body} onChange={(e)=>setLetterForm({...letterForm, body: e.target.value})} placeholder="Testo (opzionale · se vuoto verrà usato un template predefinito)"/></div>
            <div><Label>Firmatario</Label><Input className="rounded-xl mt-1" value={letterForm.sign_by} onChange={(e)=>setLetterForm({...letterForm, sign_by: e.target.value})} placeholder="Nome del firmatario"/></div>

            <Button onClick={createLetter} data-testid="letter-create" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] btn-glow">
              <FileSignature className="w-4 h-4 mr-2"/> Genera PDF lettera
            </Button>
          </div>
        </div>
      )}

      {/* UPLOAD */}
      {tab === "upload" && (
        <div className="space-y-6">
          <div className="card-smart p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FFF0E3] flex items-center justify-center flex-shrink-0"><Upload className="w-6 h-6 text-[#FF7A29]"/></div>
              <div className="flex-1">
                <h2 className="font-display text-2xl">Carica documento</h2>
                <p className="text-sm text-[#555] mt-1">Pdf o immagine · max 10MB · l'AI riconoscerà automaticamente i Codici Fiscali e ti suggerirà se inviarlo al singolo dipendente</p>
                <input type="file" accept=".pdf,image/*" onChange={onFile} data-testid="doc-upload-input" className="mt-4"/>
                {uploadFile && (
                  <div className="mt-3 bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl p-3 text-sm">
                    <strong>{uploadFile.file.name}</strong> · {(uploadFile.file.size / 1024).toFixed(1)} KB
                  </div>
                )}
                <Button onClick={doUpload} disabled={!uploadFile || uploading} data-testid="doc-upload-submit" className="mt-4 rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">
                  <Sparkles className="w-4 h-4 mr-2"/> {uploading ? "Analisi AI in corso…" : "Carica e analizza"}
                </Button>
              </div>
            </div>
          </div>

          {analysis && (
            <div className="card-smart p-6 space-y-4" data-testid="upload-analysis">
              <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FF7A29]"/><h3 className="font-display text-xl">Risultato analisi AI</h3></div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-[#FFFAF5] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#757575]">Codici Fiscali</div>
                  <div className="font-display text-3xl mt-1">{analysis.fiscal_codes_found?.length || 0}</div>
                  <div className="text-xs text-[#757575]">Trovati nel documento</div>
                </div>
                <div className="bg-[#FFFAF5] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#757575]">Dipendenti matchati</div>
                  <div className="font-display text-3xl mt-1 text-green-700">{analysis.matched_employees?.length || 0}</div>
                  <div className="text-xs text-[#757575]">Identificati in rubrica</div>
                </div>
                <div className={`rounded-xl p-4 ${analysis.should_split ? "bg-amber-50 border border-amber-200" : "bg-[#FFFAF5]"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#757575]">Azione suggerita</div>
                  <div className="font-display text-xl mt-1">{analysis.should_split ? "Spezzare" : "Documento unitario"}</div>
                </div>
              </div>
              {analysis.matched_employees?.length > 0 && (
                <div>
                  <Label>Dipendenti identificati</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {analysis.matched_employees.map((e) => (
                      <span key={e.id} className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3"/> {e.name} {e.lastname} · {e.fiscal_code}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {analysis.ai_advice && (
                <div className="bg-gradient-to-br from-[#FFF0E3] to-[#FFFAF5] border border-[#EDE5D8] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#FF7A29] mb-2">Consiglio AI</div>
                  <div className="text-sm leading-relaxed">{analysis.ai_advice}</div>
                </div>
              )}
              {analysis.should_split && analysis.matched_employees?.length > 0 && (
                <Button onClick={()=>splitSend(analysis.doc_id)} data-testid="split-send-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">
                  <Send className="w-4 h-4 mr-2"/> Spezza e invia ai {analysis.matched_employees.length} dipendenti
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* LIST */}
      {tab === "list" && (
        <div className="card-smart overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-[#EDE5D8]">
              <tr className="text-left">
                <th className="p-4">Titolo</th>
                <th className="p-4">Tipo</th>
                <th className="p-4">Creato</th>
                <th className="p-4">Stato</th>
                <th className="p-4 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="5" className="p-12 text-center text-[#757575]">Nessun documento.</td></tr>}
              {items.map((d) => (
                <tr key={d.id} className="border-b border-[#EDE5D8]" data-testid={`doc-${d.id}`}>
                  <td className="p-4 font-semibold">{d.title}</td>
                  <td className="p-4 text-xs">{d.letter_type ? LETTER_TYPES.find(t=>t.id===d.letter_type)?.label : (d.uploaded_raw ? "Upload" : d.kind?.toUpperCase())}</td>
                  <td className="p-4 text-xs text-[#757575]">{new Date(d.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="p-4 text-xs">{d.signed ? <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Firmato</span> : <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Archiviato</span>}</td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      <Button onClick={()=>downloadDoc(d)} variant="ghost" className="rounded-full h-8 text-xs hover:bg-[#FFF0E3] text-[#0A0A0A]" data-testid={`doc-download-${d.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1"/> Apri
                      </Button>
                      <Button onClick={()=>openSignDialog(d)} className="rounded-full h-8 text-xs bg-[#FF7A29] hover:bg-[#E85D00] text-white" data-testid={`doc-request-sign-${d.id}`}>
                        <FileSignature className="w-3.5 h-3.5 mr-1"/> Richiedi firma
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SIGNATURE REQUEST DIALOG */}
      {signOpen && signDoc && (
        <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" data-testid="sign-request-dialog">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#EDE5D8] px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FFF0E3] flex items-center justify-center"><FileSignature className="w-5 h-5 text-[#FF7A29]"/></div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#757575]">Richiedi firma digitale</div>
                  <div className="font-display text-lg leading-tight">{signDoc.title}</div>
                </div>
              </div>
              <button onClick={()=>setSignOpen(false)} className="text-[#757575] hover:text-[#0A0A0A]" data-testid="sign-dialog-close"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-[#FFF8F1] border border-[#EDE5D8] rounded-xl p-3 text-xs text-[#555] flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#FF7A29] flex-shrink-0 mt-0.5"/>
                <span>Ogni destinatario riceverà un'email con un link sicuro. Potrà firmare dal browser dello smartphone con il dito o con un pennino soft-touch (Apple Pencil supportato). Verrà registrato l'hash SHA-256 del documento.</span>
              </div>

              <div>
                <Label>Aggiungi destinatari (dipendenti)</Label>
                <Input
                  data-testid="sign-recipient-search"
                  className="rounded-xl mt-1"
                  value={signSearch}
                  onChange={(e)=>setSignSearch(e.target.value)}
                  placeholder="Cerca per nome, cognome o email…"
                />
                {signSearchResults.length > 0 && (
                  <div className="border border-[#EDE5D8] rounded-xl mt-1 max-h-48 overflow-auto divide-y divide-[#EDE5D8]">
                    {signSearchResults.map((e) => (
                      <button key={e.id} type="button" onClick={()=>addSignRecipient(e)} className="w-full text-left px-3 py-2 hover:bg-[#FFF0E3] flex items-center justify-between" data-testid={`sign-search-${e.id}`}>
                        <div>
                          <div className="text-sm font-semibold">{e.name} {e.lastname}</div>
                          <div className="text-xs text-[#757575]">{e.email}{e.job && ` · ${e.job}`}</div>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest text-[#FF7A29]">+ Aggiungi</span>
                      </button>
                    ))}
                  </div>
                )}
                {signRecipients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {signRecipients.map((e) => (
                      <span key={e.id} className="bg-[#FFF0E3] text-[#0A0A0A] px-3 py-1 rounded-full text-xs flex items-center gap-2" data-testid={`sign-recipient-${e.id}`}>
                        {e.name} {e.lastname}
                        <button onClick={()=>removeSignRecipient(e.id)} className="text-[#E85D00] hover:text-red-600"><X className="w-3 h-3"/></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>Nota (opzionale)</Label>
                <Textarea data-testid="sign-note" className="rounded-xl mt-1" value={signNote} onChange={(e)=>setSignNote(e.target.value)} placeholder="Es. Si prega di firmare entro venerdì."/>
              </div>

              <Button onClick={submitSignatureRequests} disabled={signSubmitting || signRecipients.length === 0} data-testid="sign-send-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] btn-glow w-full sm:w-auto">
                <Send className="w-4 h-4 mr-2"/> {signSubmitting ? "Invio in corso…" : `Invia richiesta a ${signRecipients.length || ""} destinatari`}
              </Button>

              {/* Esistenti */}
              {(docSignatureRequests[signDoc.id] || []).length > 0 && (
                <div className="border-t border-[#EDE5D8] pt-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-2">Richieste già inviate</div>
                  <div className="space-y-2">
                    {(docSignatureRequests[signDoc.id] || []).map((r) => (
                      <div key={r.id} className="border border-[#EDE5D8] rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`sign-existing-${r.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{r.signer_full_name}</div>
                          <div className="text-xs text-[#757575] truncate">{r.signer_email || "—"}</div>
                          <div className="text-[10px] text-[#999] mt-1">Inviata il {new Date(r.created_at).toLocaleDateString("it-IT")} {new Date(r.created_at).toLocaleTimeString("it-IT", {hour: "2-digit", minute: "2-digit"})}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {r.status === "signed" ? (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3"/> Firmato
                            </span>
                          ) : r.status === "revoked" ? (
                            <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Revocato</span>
                          ) : (
                            <>
                              <button onClick={()=>copySignLink(r)} className="text-xs text-[#0A0A0A] hover:text-[#E85D00] flex items-center gap-1" data-testid={`sign-copy-${r.id}`}><Copy className="w-3 h-3"/> Link</button>
                              <button onClick={()=>revokeSignReq(r)} className="text-xs text-red-600 hover:underline" data-testid={`sign-revoke-${r.id}`}>Revoca</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB COMUNICAZIONI · archivio per tutti */}
      {tab === "communications" && (
        <div data-testid="communications-tab">
          {communications.length === 0 ? (
            <div className="card-smart p-12 text-center text-[#757575]">
              <Archive className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>
              Nessuna comunicazione presente.
            </div>
          ) : (
            <div className="space-y-2">
              {communications.map((c) => (
                <div key={c.id} className="card-smart p-4 flex items-center justify-between gap-3" data-testid={`comm-${c.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-[#FFF0E3] text-[#FF7A29] flex items-center justify-center"><Archive className="w-5 h-5"/></div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.title}</div>
                      <div className="text-xs text-[#757575]">{new Date(c.created_at).toLocaleString("it-IT")} · {c.kind?.toUpperCase()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* DIALOG BROADCAST (solo sub_admin) */}
      {broadcastOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={()=>setBroadcastOpen(false)} data-testid="broadcast-dialog">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e)=>e.stopPropagation()}>
            <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Radio className="w-6 h-6 text-[#FF7A29]"/> Invia documento</h2>
            <div className="space-y-4">
              <div>
                <Label>Titolo della comunicazione *</Label>
                <Input className="rounded-xl mt-1" value={broadcastTitle} onChange={(e)=>setBroadcastTitle(e.target.value)} placeholder="Es. Verbale assemblea sindacale 17/05/2026" data-testid="broadcast-title"/>
              </div>
              <div>
                <Label>File (PDF/Immagine) *</Label>
                <Input type="file" accept="application/pdf,image/*" className="rounded-xl mt-1" onChange={(e)=>setBroadcastFile(e.target.files?.[0] || null)} data-testid="broadcast-file"/>
              </div>
              <div>
                <Label>Destinatari</Label>
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={()=>setBroadcastMode("all")} data-testid="bcast-mode-all" className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold border-2 ${broadcastMode === "all" ? "bg-[#FF7A29] text-white border-[#FF7A29]" : "border-[#EDE5D8]"}`}>
                    Tutti i dipendenti
                  </button>
                  <button type="button" onClick={()=>setBroadcastMode("single")} data-testid="bcast-mode-single" className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold border-2 ${broadcastMode === "single" ? "bg-[#FF7A29] text-white border-[#FF7A29]" : "border-[#EDE5D8]"}`}>
                    Singolo dipendente
                  </button>
                </div>
              </div>
              {broadcastMode === "single" && (
                <div>
                  <Label>Cerca dipendente per nome/email</Label>
                  <Input
                    className="rounded-xl mt-1"
                    value={broadcastEmpSearch}
                    onChange={(e)=>setBroadcastEmpSearch(e.target.value)}
                    placeholder="Digita almeno 2 caratteri…"
                    data-testid="broadcast-search"
                  />
                  {broadcastEmpResults.length > 0 && (
                    <div className="border border-[#EDE5D8] rounded-xl mt-1 max-h-40 overflow-auto">
                      {broadcastEmpResults.map((e) => (
                        <button
                          type="button"
                          key={e.id}
                          onClick={()=>{ setBroadcastEmpId(e.id); setBroadcastEmpSearch(`${e.name} ${e.lastname}`); setBroadcastEmpResults([]); }}
                          data-testid={`bcast-pick-${e.id}`}
                          className={`w-full text-left px-3 py-2 hover:bg-[#FFF0E3] text-sm border-b border-[#EDE5D8] last:border-0 ${broadcastEmpId === e.id ? "bg-[#FFF0E3]" : ""}`}
                        >
                          <div className="font-semibold">{e.name} {e.lastname}</div>
                          <div className="text-xs text-[#757575]">{e.email}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {broadcastEmpId && <div className="text-xs text-green-700 mt-1">✓ Destinatario selezionato</div>}
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={()=>setBroadcastOpen(false)} className="rounded-full">Annulla</Button>
                <Button onClick={submitBroadcast} disabled={broadcastSending} data-testid="broadcast-send" className="rounded-full bg-[#FF7A29] hover:bg-black">
                  <Send className="w-4 h-4 mr-2"/> {broadcastSending ? "Invio…" : "Invia"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
